import { NgClass } from '@angular/common';
import { Component, inject, Input, ViewChild } from '@angular/core';
import PrimeOverlayComponent from '@iupics-components/overrided/prime-overlay/prime-overlay.component';
import type { AbstractDataContainer } from '@iupics-manager/models/abstract-datacontainer';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';

/**
 * Per-field conflict popover: local vs server value, anchored under (or above) the field.
 * Choosing calls the field's chooseData with the same values as before.
 */
@Component({
  selector: 'iu-field-conflict-panel',
  templateUrl: './field-conflict-panel.component.html',
  styleUrls: ['./field-conflict-panel.component.scss'],
  standalone: true,
  imports: [NgClass, ButtonModule, TranslateModule, PrimeOverlayComponent],
})
export default class FieldConflictPanelComponent {
  private translate = inject(TranslateService);

  /** the field owning the conflict */
  @Input() field: AbstractDataContainer;

  @ViewChild('overlay', { static: true })
  overlay: PrimeOverlayComponent;

  private icon: HTMLElement = null;
  private anchor: HTMLElement = null;

  readonly alignFn = (ctx: PrimeOverlayComponent) => this.place(ctx);

  get overlayVisible(): boolean {
    return !!this.overlay?.overlayVisible;
  }

  /** Open/close from the conflict icon (click, Enter or Space) */
  toggle(event: Event): void {
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    if (this.overlayVisible) {
      event?.stopPropagation();
      this.hide();
      return;
    }
    this.show(event);
  }

  show(event: Event): void {
    const icon = ((event?.currentTarget || event?.target) as HTMLElement) ?? null;
    this.icon = icon;
    this.anchor = this.resolveAnchor(icon);
    this.overlay.show(event, this.anchor ?? icon);
  }

  hide(): void {
    this.overlay?.hide();
  }

  /* --- content --- */

  get label(): string {
    return this.field?.label || this.field?.data?.label || this.field?.data?.columnName || '';
  }

  get localValue(): any {
    const col = this.field?.data?.columnName;
    return col ? this.field?.dataStored?.data?.[col] : undefined;
  }

  get remoteValue(): any {
    return this.field?.conflictedData;
  }

  /** title / aria-label of the field's conflict icon */
  get iconTooltip(): string {
    return this.translate.instant('fieldConflict.iconTooltip');
  }

  get canOpenMergeTool(): boolean {
    return !!this.field?.editViewParent;
  }

  /** same values and flags as the former inline links */
  keep(event: MouseEvent, side: 'local' | 'remote'): void {
    if (!this.field) {
      return;
    }
    if (side === 'local') {
      this.field.chooseData(event, this.localValue, true);
    } else {
      this.field.chooseData(event, this.remoteValue, false);
    }
    this.hide();
  }

  openMergeTool(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.hide();
    if (this.field?.editViewParent) {
      this.field.editViewParent.wantToMergeConflicts = true;
    }
  }

  /** Readable value, null when empty */
  format(value: any): string {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const data = this.field?.data;
    if (data?.fieldType === 'password') {
      return '********';
    }
    if (value instanceof Object) {
      if ('displayValue' in value) {
        const display = value['displayValue'];
        return display === undefined || display === null || display === '' ? null : String(display);
      }
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    const component = data?.componentName;
    if (component === 'InputSwitchUiComponent' || typeof value === 'boolean') {
      const yes = value === true || String(value).toUpperCase() === 'Y';
      return this.translate.instant(yes ? 'generic.yes' : 'generic.no');
    }
    if (component === 'CalendarUiComponent') {
      return this.formatDate(value, !!data?.needTime);
    }
    return String(value);
  }

  private formatDate(value: any, withTime: boolean): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return String(value);
    }
    try {
      const lang = (this.translate.currentLang || '').replace('_', '-') || undefined;
      const options: Intl.DateTimeFormatOptions = withTime
        ? { dateStyle: 'short', timeStyle: 'short' }
        : { dateStyle: 'short' };
      return new Intl.DateTimeFormat(lang, options).format(date);
    } catch {
      return date.toLocaleString();
    }
  }

  /* --- positioning --- */

  /** The visible input box next to the icon, else the icon's wrapper */
  private resolveAnchor(icon: HTMLElement): HTMLElement {
    const host = icon?.parentElement;
    if (!host) {
      return icon;
    }
    const candidates = host.querySelectorAll<HTMLElement>(
      'input:not([type=hidden]), textarea, .p-inputtext, .p-checkbox, iu-checkbox-ui'
    );
    for (const el of Array.from(candidates)) {
      if (el.offsetWidth > 0 && el.offsetHeight > 0) {
        return el;
      }
    }
    return host;
  }

  /** Below the field (above if no room), aligned on its left (or right) edge, arrow on the icon */
  private place(ctx: PrimeOverlayComponent): void {
    const container: HTMLElement = ctx?.container;
    const anchor = this.anchor ?? this.icon;
    if (!container || !anchor) {
      return;
    }
    const margin = 8;
    const gap = 10;
    const doc = container.ownerDocument;
    const win = doc.defaultView;
    const viewW = doc.documentElement.clientWidth;
    const viewH = win.innerHeight;
    const a = anchor.getBoundingClientRect();
    const i = (this.icon ?? anchor).getBoundingClientRect();
    const w = container.offsetWidth;
    const h = container.offsetHeight;

    let top = a.bottom + gap;
    let flipped = false;
    if (top + h > viewH - margin && a.top - gap - h >= margin) {
      top = a.top - gap - h;
      flipped = true;
    }
    let left = a.left;
    if (left + w > viewW - margin) {
      left = a.right - w;
    }
    left = Math.max(margin, Math.min(left, viewW - w - margin));

    container.style.top = top + win.scrollY + 'px';
    container.style.left = left + win.scrollX + 'px';
    container.style.width = '';
    container.style.height = '';
    container.style.marginTop = '0';
    container.style.transformOrigin = flipped ? 'bottom' : 'top';
    container.classList.remove('p-overlaypanel-flipped');
    container.classList.toggle('iu-field-conflict-overlay-flipped', flipped);

    const arrow = Math.max(14, Math.min(i.left + i.width / 2 - left, w - 14));
    container.style.setProperty('--iu-field-conflict-arrow-left', arrow + 'px');
  }
}
